/** Collects string entries from API `errors` / `errorMessages` arrays. */
export function getErrorMessagesFromUnknown(value: unknown): string[] {
	if (!value || typeof value !== "object") return [];
	const data = value as Record<string, unknown>;
	const errors = data.errors ?? data.errorMessages;
	if (!Array.isArray(errors)) return [];
	return errors
		.filter(
			(item): item is string =>
				typeof item === "string" && item.trim().length > 0,
		)
		.map((item) => item.trim());
}

/** Pulls user-facing detail from API error nesting. */
export function getApiErrorDetail(error: unknown): string | null {
	if (!error || typeof error !== "object") {
		if (error instanceof Error && error.message.trim()) {
			return error.message.trim();
		}
		return null;
	}

	const errorRecord = error as Record<string, unknown>;
	const cause =
		errorRecord.cause && typeof errorRecord.cause === "object"
			? (errorRecord.cause as Record<string, unknown>)
			: null;
	const nestedCause =
		cause?.cause && typeof cause.cause === "object"
			? (cause.cause as Record<string, unknown>)
			: null;

	const messageLists = [
		getErrorMessagesFromUnknown(errorRecord),
		getErrorMessagesFromUnknown(errorRecord.data),
		getErrorMessagesFromUnknown(cause),
		getErrorMessagesFromUnknown(cause?.data),
		getErrorMessagesFromUnknown(nestedCause),
		getErrorMessagesFromUnknown(nestedCause?.data),
	];

	for (const list of messageLists) {
		if (list.length > 0) return list.join("; ");
	}

	const causeMessage =
		cause && typeof cause.message === "string" ? cause.message.trim() : "";
	if (causeMessage) return causeMessage;

	const topMessage =
		typeof errorRecord.message === "string" ? errorRecord.message.trim() : "";
	if (topMessage) return topMessage;

	return null;
}

/** Keeps toast body compact so Sonner does not measure/clip a viewport-tall banner. */
export function formatApiErrorToastDescription(detail: string): string {
	const parts = detail
		.split(";")
		.map((part) => part.trim())
		.filter(Boolean);
	const shown = parts.slice(0, 3);
	const joined = shown.join(" · ");
	const maxLen = 240;
	if (joined.length <= maxLen) {
		return parts.length > shown.length ? `${joined} · …` : joined;
	}
	return `${joined.slice(0, maxLen - 1)}…`;
}
